import { useState, useEffect } from 'react'
import { Button, Card, CardContent, CardHeader, CardTitle, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, Badge, Input, Label, Select, Textarea } from '../components/ui'
import { useCompany } from '../hooks/useCompany'
import { useAuth } from '../hooks/useAuth'

interface PTORequest {
  id: string
  user_id: string
  user_name: string
  user_email: string
  team_id: string
  type: 'vacation' | 'sick' | 'personal' | 'other'
  start_date: string
  end_date: string
  hours_requested: number
  reason: string
  status: 'pending' | 'approved' | 'rejected'
  reviewed_by: string | null
  reviewed_at: number | null
  created_at: number
  updated_at: number
}

interface PTOBalance {
  user_id: string
  vacation_hours: number
  sick_hours: number
  personal_hours: number
  total_available: number
  total_used: number
}

export default function PTO() {
  const { currentCompany } = useCompany()
  const { user } = useAuth()
  const [requests, setRequests] = useState<PTORequest[]>([])
  const [balance, setBalance] = useState<PTOBalance | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showNewRequest, setShowNewRequest] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [currentTeamId, setCurrentTeamId] = useState<string | null>(null)
  const [newRequest, setNewRequest] = useState({
    type: 'vacation',
    start_date: '',
    end_date: '',
    reason: ''
  })

  // Fetch teams for current company
  useEffect(() => {
    const fetchTeamsAndSetCurrent = async () => {
      if (!currentCompany?.id) {
        setCurrentTeamId(null)
        setLoading(false)
        return
      }

      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_BASE_URL}/v1/teams?company_id=${currentCompany.id}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
            }
          }
        )

        if (!response.ok) {
          throw new Error('Failed to fetch teams')
        }

        const data = await response.json()
        const teams = data.teams || []
        
        // Find a team where the user is a member
        const userTeam = teams.find((team: any) => team.user_role !== null)
        
        if (userTeam) {
          setCurrentTeamId(userTeam.id)
        } else if (teams.length > 0) {
          setCurrentTeamId(teams[0].id)
        } else {
          setCurrentTeamId(null)
          setError('No teams found in this company')
        }
      } catch (err) {
        console.error('Failed to fetch teams:', err)
        setCurrentTeamId(null)
        setError('Failed to load teams')
      }
    }

    fetchTeamsAndSetCurrent()
  }, [currentCompany?.id])

  // Fetch PTO data
  useEffect(() => {
    if (!currentTeamId) {
      return
    }

    fetchPTOData()
  }, [currentTeamId])

  const fetchPTOData = async () => {
    if (!currentTeamId || !user) return

    try {
      setLoading(true)
      setError(null)

      // For now, just set empty data since the API endpoints don't exist yet
      setRequests([])
      setBalance({
        user_id: user.id,
        vacation_hours: 80,
        sick_hours: 40,
        personal_hours: 16,
        total_available: 136,
        total_used: 0
      })
    } catch (err) {
      console.error('Failed to fetch PTO data:', err)
      setError(err instanceof Error ? err.message : 'Failed to load PTO data')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmitRequest = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!newRequest.start_date || !newRequest.end_date) {
      setError('Please select start and end dates')
      return
    }

    if (new Date(newRequest.start_date) > new Date(newRequest.end_date)) {
      setError('End date must be after start date')
      return
    }

    try {
      setSubmitting(true)
      setError(null)

      // Calculate hours requested (8 hours per day for now)
      const start = new Date(newRequest.start_date)
      const end = new Date(newRequest.end_date)
      const days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1
      const hoursRequested = days * 8

      // For now, just add to local state
      const mockRequest: PTORequest = {
        id: Date.now().toString(),
        user_id: user!.id,
        user_name: user!.firstName + ' ' + user!.lastName || user!.email || '',
        user_email: user!.email || '',
        team_id: currentTeamId!,
        type: newRequest.type as PTORequest['type'],
        start_date: newRequest.start_date,
        end_date: newRequest.end_date,
        hours_requested: hoursRequested,
        reason: newRequest.reason,
        status: 'pending',
        reviewed_by: null,
        reviewed_at: null,
        created_at: Date.now(),
        updated_at: Date.now()
      }

      setRequests([mockRequest, ...requests])
      setShowNewRequest(false)
      setNewRequest({
        type: 'vacation',
        start_date: '',
        end_date: '',
        reason: ''
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit request')
    } finally {
      setSubmitting(false)
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge variant="success">Approved</Badge>
      case 'rejected':
        return <Badge variant="danger">Rejected</Badge>
      default:
        return <Badge variant="warning">Pending</Badge>
    }
  }

  const getTypeBadge = (type: string) => {
    switch (type) {
      case 'vacation':
        return <Badge variant="default">Vacation</Badge>
      case 'sick':
        return <Badge variant="secondary">Sick</Badge>
      case 'personal':
        return <Badge variant="secondary">Personal</Badge>
      default:
        return <Badge variant="secondary">{type}</Badge>
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-48 mb-6"></div>
          <div className="space-y-4">
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (error && !currentTeamId) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="text-red-600 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to load PTO</h3>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Paid Time Off</h1>
        <Button onClick={() => setShowNewRequest(true)}>
          Request Time Off
        </Button>
      </div>

      {/* PTO Balance */}
      {balance && (
        <Card>
          <CardHeader>
            <CardTitle>Your PTO Balance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-600">Vacation</p>
                <p className="text-2xl font-bold">{balance.vacation_hours}h</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Sick</p>
                <p className="text-2xl font-bold">{balance.sick_hours}h</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Personal</p>
                <p className="text-2xl font-bold">{balance.personal_hours}h</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Available</p>
                <p className="text-2xl font-bold text-green-600">{balance.total_available}h</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* New Request Form */}
      {showNewRequest && (
        <Card>
          <CardHeader>
            <CardTitle>New Time Off Request</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmitRequest} className="space-y-4">
              <div>
                <Label htmlFor="type">Type</Label>
                <Select
                  id="type"
                  value={newRequest.type}
                  onChange={(e) => setNewRequest({ ...newRequest, type: e.target.value })}
                  className="w-full"
                >
                  <option value="vacation">Vacation</option>
                  <option value="sick">Sick Leave</option>
                  <option value="personal">Personal Time</option>
                  <option value="other">Other</option>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="start_date">Start Date</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={newRequest.start_date}
                    onChange={(e) => setNewRequest({ ...newRequest, start_date: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="end_date">End Date</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={newRequest.end_date}
                    onChange={(e) => setNewRequest({ ...newRequest, end_date: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="reason">Reason (Optional)</Label>
                <Textarea
                  id="reason"
                  value={newRequest.reason}
                  onChange={(e) => setNewRequest({ ...newRequest, reason: e.target.value })}
                  rows={3}
                  placeholder="Provide any additional details..."
                />
              </div>

              {error && (
                <div className="text-red-600 text-sm">{error}</div>
              )}

              <div className="flex gap-2">
                <Button type="submit" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit Request'}
                </Button>
                <Button type="button" variant="secondary" onClick={() => {
                  setShowNewRequest(false)
                  setError(null)
                }}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Requests Table */}
      <Card>
        <CardHeader>
          <CardTitle>Your Requests</CardTitle>
        </CardHeader>
        <CardContent>
          {requests.length === 0 ? (
            <p className="text-gray-600 text-center py-8">No PTO requests yet</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Dates</TableHead>
                  <TableHead>Hours</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Submitted</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {requests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>{getTypeBadge(request.type)}</TableCell>
                    <TableCell>
                      {new Date(request.start_date).toLocaleDateString()} - {new Date(request.end_date).toLocaleDateString()}
                    </TableCell>
                    <TableCell>{request.hours_requested}h</TableCell>
                    <TableCell className="max-w-xs truncate">{request.reason || '-'}</TableCell>
                    <TableCell>{getStatusBadge(request.status)}</TableCell>
                    <TableCell>{new Date(request.created_at).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}